import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { track } from "../lib/analytics";

function renderInline(text) {
  // Split on **bold** markers
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(part);
    if (m) return <strong key={idx}>{m[1]}</strong>;
    return part;
  });
}

function renderLongDescription(text) {
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let buffer = [];
  let mode = null;

  const flush = () => {
    if (buffer.length === 0) return;
    if (mode === "ul") {
      blocks.push({ type: "ul", items: buffer });
    } else {
      blocks.push({ type: "p", text: buffer.join(" ") });
    }
    buffer = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line === "") { flush(); mode = null; continue; }
    const isBullet = /^[•\-*]\s+/.test(line);
    if (isBullet) {
      if (mode !== "ul") { flush(); mode = "ul"; }
      buffer.push(line.replace(/^[•\-*]\s+/, ""));
    } else {
      if (mode !== "p") { flush(); mode = "p"; }
      buffer.push(line);
    }
  }
  flush();

  return blocks.map((b, i) => {
    if (b.type === "ul") {
      return (
        <ul key={i} className="font-body mt-4 mb-4 space-y-2" style={{ color: "var(--opus-text)", fontSize: "1.05rem", lineHeight: 1.7, paddingLeft: "1.25rem", listStyle: "disc" }}>
          {b.items.map((it, j) => <li key={j} style={{ paddingLeft: "0.25rem" }}>{renderInline(it)}</li>)}
        </ul>
      );
    }
    return (
      <p key={i} className="font-body mt-5 leading-[1.8]" style={{ color: "var(--opus-text)", fontSize: "1.05rem", fontWeight: /:\s*$/.test(b.text) ? 700 : 400 }}>
        {renderInline(b.text)}
      </p>
    );
  });
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);
  const videoEndRef = useRef(0);

  useEffect(() => {
    track("page_view");
    api.get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        if (r.data?.id) track("product_view", { product_id: r.data.id });
      })
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-3xl mx-auto py-32 text-center text-xs tracking-[0.3em] uppercase" style={{ color: "var(--opus-text-muted)" }}>
          Loading…
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-3xl mx-auto py-32 text-center">
          <p className="font-display text-3xl">Resource not found.</p>
          <Link to="/" className="text-xs tracking-[0.3em] uppercase underline mt-4 inline-block" style={{ color: "var(--opus-sage)" }}>Back to Resources</Link>
        </div>
      </div>
    );
  }

  const isFree = Number(product.price) === 0;
  const priceLabel = isFree ? "FREE" : (Number.isInteger(product.price) ? `$${product.price}` : `$${product.price.toFixed(2)}`);
  const hasExternalUrl = Boolean(product.external_url && product.external_url.trim());

  return (
    <div className="App">
      <Header />      <section
        className="border-b"
        style={{ background: "var(--opus-bg-secondary)", borderColor: "var(--opus-border)" }}
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <button
            onClick={() => nav(-1)}
            data-testid="back-link"
            className="text-[0.7rem] tracking-[0.3em] uppercase mb-10 inline-flex items-center gap-2"
            style={{ color: "var(--opus-sage)" }}
          >
            ← Back to Resources
          </button>
          <h1
            data-testid="detail-title"
            className="font-display leading-[1.05]"
            style={{ color: "var(--opus-text)", fontSize: "clamp(2.2rem, 5vw, 3.6rem)", fontWeight: 500, letterSpacing: "0.01em" }}
          >
            {product.title}
          </h1>
          <span className="opus-rule mt-7 block" />
          <p data-testid="detail-short" className="font-body italic mt-6 leading-relaxed max-w-2xl" style={{ color: "var(--opus-text-secondary)", fontSize: "1.15rem" }}>
            {product.short_description}
          </p>
        </div>
      </section>

      {product.video_url ? (
        <section className="max-w-5xl mx-auto px-6 md:px-12 pt-12 md:pt-16" data-testid="detail-video-section">
          <div className="opus-card overflow-hidden opus-fade-up">
            <video
              ref={videoRef}
              data-testid="detail-video"
              src={product.video_url}
              autoPlay
              loop
              muted
              playsInline
              controls
              preload="auto"
              onLoadedMetadata={(e) => { videoEndRef.current = Math.max(0, (e.currentTarget.duration || 0) - 3); }}
              onTimeUpdate={(e) => {
                const v = e.currentTarget;
                if (videoEndRef.current > 0 && v.currentTime >= videoEndRef.current) {
                  v.currentTime = 0;
                }
              }}
              className="w-full block"
              style={{ background: "transparent", maxHeight: "780px" }}
            />
            <p className="px-6 py-3 text-[0.7rem] tracking-[0.28em] uppercase font-body" style={{ color: "var(--opus-text-muted)" }}>
              Quick preview
            </p>
          </div>
        </section>
      ) : null}

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-20 grid md:grid-cols-5 gap-10 md:gap-14">
        <div className="md:col-span-3 opus-fade-up">
          <p className="text-[0.7rem] tracking-[0.32em] uppercase" style={{ color: "var(--opus-sage)" }}>What's inside</p>
          {product.long_description ? (
            <div data-testid="detail-long">
              {renderLongDescription(product.long_description)}
            </div>
          ) : (
            <p className="font-body mt-5 leading-[1.8]" style={{ color: "var(--opus-text-secondary)", fontSize: "1.05rem" }}>
              {product.short_description}
            </p>
          )}
        </div>

        <div className="md:col-span-2 opus-fade-up" style={{ animationDelay: "150ms" }}>
          <div className="opus-card p-7 md:sticky md:top-8">
            <p className="text-[0.7rem] tracking-[0.32em] uppercase" style={{ color: "var(--opus-text-muted)" }}>
              {isFree ? "Free" : "One-time purchase"}
            </p>
            <p
              className="font-display mt-3"
              style={{
                color: "var(--opus-text)",
                fontSize: isFree ? "2rem" : "3rem",
                fontWeight: 500,
                lineHeight: 1,
                letterSpacing: isFree ? "0.08em" : "0.01em",
              }}
              data-testid="detail-price"
            >
              {priceLabel}
            </p>

            {hasExternalUrl ? (
              <>
                <a
                  href={product.external_url}
                  data-testid="external-buy"
                  onClick={() => track("external_click", { product_id: product.id })}
                  className="btn-sage w-full inline-flex items-center justify-center gap-2 mt-7 px-7 py-3.5 text-[0.72rem] tracking-[0.3em] uppercase"
                  style={{ borderRadius: "2px" }}
                >
                  {isFree ? "Get it now ↗" : "Buy & Download ↗"}
                </a>
              </>
            ) : (
              <p className="text-sm mt-7 font-body italic" style={{ color: "var(--opus-text-muted)" }}>
                Coming soon. Check back shortly.
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
